import React, { useEffect, useRef, useState } from "react";

/**
 * Lightweight scroll-reveal hook + Reveal component.
 * Uses IntersectionObserver — no animation library required.
 *
 *   <Reveal>...</Reveal>            // fade-up
 *   <Reveal y={60} delay={120}>...  // custom offset + delay
 *   <Reveal as="span" once={false}> // re-trigger on every entry
 */
export function useReveal({ threshold = 0.15, once = true, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (
      typeof window === "undefined" ||
      !("IntersectionObserver" in window) ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold, once, rootMargin]);

  return [ref, visible];
}

export default function Reveal({
  children,
  as: Tag = "div",
  delay = 0,
  duration = 800,
  y = 32,
  x = 0,
  scale = 1,
  blur = 0,
  className = "",
  style = {},
  once = true,
  threshold = 0.15,
  ...rest
}) {
  const [ref, visible] = useReveal({ once, threshold });
  const reveal = {
    transition: `transform ${duration}ms cubic-bezier(0.22, 0.8, 0.32, 1) ${delay}ms, opacity ${duration}ms ease ${delay}ms, filter ${duration}ms ease ${delay}ms`,
    transform: visible
      ? "translate3d(0,0,0) scale(1)"
      : `translate3d(${x}px, ${y}px, 0) scale(${scale === 1 ? 1 : scale})`,
    opacity: visible ? 1 : 0,
    filter: visible ? "blur(0)" : `blur(${blur}px)`,
    willChange: "transform, opacity",
  };
  return (
    <Tag ref={ref} className={className} style={{ ...reveal, ...style }} {...rest}>
      {children}
    </Tag>
  );
}
